import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import { AccountTypes, FlowTypes, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import {
  closeSpinner,
  Go,
  LogError,
  LogInfo,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import { selectClientAccountsLoaded } from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectGiftingEligibility } from '@store/selectors/eligibility/gifting/gifting-eligibility.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, Observable, of, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GiftingEligibilityGuard implements CanActivate {
  constructor(
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    return this.globalStore.pipe(
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      withLatestFrom(this.globalStore.select(selectGiftingEligibility)),
      map(([, { isGiftingEligible, accountFromType, filterAccounts }]) => {
        if (isGiftingEligible) {
          this.globalStore.dispatch(
            UpdateDisplayedAccounts({
              payload: filterAccounts,
            }),
          );
          this.globalStore.dispatch(
            LogInfo({
              payload: {
                eventId: 'gifting-guard--eligible-accounts-found',
                message: `Gifting Eligibility Guard eligibility success ${FlowTypes.GIFTING} ${accountFromType} account`,
                eligibleFromType: accountFromType,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
          this.globalStore.dispatch(closeSpinner());
        } else {
          this.globalStore.dispatch(Go({ path: [`${urlMapper[FlowTypes.GIFTING]}/ineligible`] }));

          this.globalStore.dispatch(
            LogError({
              payload: {
                eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                message: `No eligible gifting ${accountFromType} FROM account`,
                ineligibleFromAccountType: accountFromType,
                flowType: FlowTypes.GIFTING,
              },
            }),
          );

          this.globalStore.dispatch(
            TrackProcessError({
              errorCode: `coo-gifting-ineligible-from-${accountFromType}_account`,
            }),
          );
        }
        return isGiftingEligible;
      }),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'gifting-guard--unknown-error',
              message: `Gifting Eligibility Guard failed due to unknown error. ${err.message}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        this.globalStore.dispatch(
          TrackProcessError({
            errorCode: `coo-gifting-ineligible-app_error`,
          }),
        );

        return of(false);
      }),
    );
  }
}
